import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Phone, Mail, MapPin, Linkedin, Twitter, Instagram, Globe } from 'lucide-react';
import { Logo } from './Logo';
import klerosIconWhite from '/images/kleros-icon-white.png';
import blfIconWhite from '/images/blf-icon-white.png';

export const Footer: React.FC = () => {
  const { t, language, setLanguage } = useLanguage();

  const contactInfo = [
    {
      icon: Phone,
      label: '+1 (829) 748-9261',
      href: 'tel:+18297489261',
    },
    {
      icon: Mail,
      label: 'j.bisono@jorgeprats.com',
      href: 'mailto:j.bisono@jorgeprats.com',
    },
    {
      icon: MapPin,
      label: 'Torre Forum - Local 8-A, Av. 27 de Febrero, #495 El Millón, Santo Domingo',
      href: 'https://maps.google.com/?q=Torre+Forum+Av+27+de+Febrero+El+Millón+Santo+Domingo+República+Dominicana',
    },
  ];

  const socialLinks = [
    {
      icon: Twitter,
      label: 'Twitter',
      href: 'https://x.com/joanbisonom',
      color: 'hover:text-sky-500',
    },
    {
      icon: Linkedin,
      label: 'LinkedIn',
      href: 'https://www.linkedin.com/in/joanbisonom',
      color: 'hover:text-blue-600',
    },
    {
      icon: Instagram,
      label: 'Instagram',
      href: 'https://www.instagram.com/joanbisonom',
      color: 'hover:text-pink-500',
    },
  ];

  const navLinks = [
    { path: '/', label: t('home') },
    { path: '/about', label: t('aboutMe') },
    { path: '/blog', label: t('blog') },
    { path: '/multimedia', label: t('multimedia') },
    { path: '/contact', label: t('contact') },
  ];

  // Los documentos legales solo existen en español: sus nombres también.
  const legalLinks = [
    { path: '/privacy', label: 'Política de privacidad' },
    { path: '/aviso-legal', label: 'Aviso legal y condiciones de uso' },
    { path: '/reembolsos', label: 'Política de reembolsos' },
    { path: '/cookies', label: 'Política de cookies' },
    { path: '/condiciones-corporativo', label: 'Condiciones del plan Corporativo' },
    { path: '/privacidad', label: 'Privacidad de Clerk' },
    { path: 'https://clerk.do/', label: 'Clerk' },
  ];

  return (
    <footer className="bg-charcoal-800 text-charcoal-100 border-t border-charcoal-700">
      {/* Abajo, sitio para el botón flotante de llamar: en el móvil tapaba los enlaces. */}
      <div className="container mx-auto px-6 pt-12 pb-24 md:pb-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          {/* Brand & Description */}
          <div className="space-y-4">
            <Link to="/" className="flex items-center gap-2 w-fit">
              <Logo className="h-10 w-auto" />
              <h3 className="text-2xl font-semibold text-white">Joan Bisonó</h3>
            </Link>
            <p className="text-charcoal-300 text-sm leading-relaxed">
              {t('footerDescription')}
            </p>
            
            {/* Social Links */}
            <div className="flex gap-3 pt-2">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`p-3 bg-charcoal-700 rounded-lg transition-colors ${social.color}`}
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>

            {/* Affiliations */}
            <div className="flex items-center gap-4 pt-2">
              <a
                href="https://kleros.io/rd-fellowship/joan-bison%C3%B3-mej%C3%ADa#fellow-profile"
                target="_blank"
                rel="noopener noreferrer"
                className="opacity-70 hover:opacity-100 transition-opacity"
                aria-label="Kleros"
              >
                <img src={klerosIconWhite} alt="Kleros" className="h-7 w-7 object-contain" loading="lazy" draggable={false} />
              </a>
              <a
                href="https://blf.io"
                target="_blank"
                rel="noopener noreferrer"
                className="opacity-70 hover:opacity-100 transition-opacity"
                aria-label="Blockchain Lawyers Forum"
              >
                <img src={blfIconWhite} alt="Blockchain Lawyers Forum" className="h-7 w-7 object-contain" loading="lazy" draggable={false} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-white">{t('quickLinks')}</h4>
            <ul className="space-y-2">
              {navLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-charcoal-300 hover:text-white transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Information */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-white">{t('contactInfo')}</h4>
            <ul className="space-y-3">
              {contactInfo.map((contact) => (
                <li key={contact.label}>
                  <a
                    href={contact.href}
                    target={contact.href.startsWith('http') ? '_blank' : undefined}
                    rel={contact.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="flex items-center gap-3 text-charcoal-300 hover:text-white transition-colors text-sm group"
                  >
                    <span className="p-2 bg-charcoal-700 rounded-lg group-hover:bg-charcoal-600 transition-colors">
                      <contact.icon className="w-4 h-4" />
                    </span>
                    <span>{contact.label}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar, a lo X: una fila de enlaces que se parte sola, el
            idioma y el titular (Joan, 2026-10-10, para la accesibilidad). */}
        <div className="pt-8 border-t border-charcoal-700 space-y-4">
          {/* ⚠️ El enlace a la política va en el pie de TODAS las páginas: es
              una de las páginas que un agente busca para decidir si el sitio
              es de alguien real, y una que no se enlaza desde ningún sitio
              cuesta encontrarla igual que si no existiera. */}
          <nav aria-label={t('footerLinksLabel')}>
            <ul className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-sm">
              {legalLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-charcoal-300 hover:text-white hover:underline underline-offset-4 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-steel-blue rounded"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm">
            {/* Un <select> nativo: teclado, lector de pantalla y móvil sin
                escribir nada para ellos. El idioma también se cambia arriba. */}
            <label className="flex items-center gap-2 text-charcoal-300">
              <Globe className="w-4 h-4" aria-hidden="true" />
              <span>{t('languageLabel')}</span>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value as typeof language)}
                className="bg-charcoal-700 text-white border border-charcoal-600 rounded-md px-2 py-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-steel-blue"
              >
                <option value="es" lang="es">Español</option>
                <option value="en" lang="en">English</option>
                <option value="fr" lang="fr">Français</option>
              </select>
            </label>
            <p className="text-charcoal-400">
              © {new Date().getFullYear()} Joan Bisonó. {t('allRightsReserved')}
            </p>
          </div>
          {/* ⚠️ Aquí decía «Página desarrollada por Bisono Legaltech
              Solutions, S.R.L.» y se quitó (Joan, 2026-09-30): el titular del
              sitio y de Clerk es Joan como persona física, que es lo que
              declaran el aviso legal y las dos políticas. Nombrar una sociedad
              en el pie contradecía esos documentos. Cuando exista la LLC, el
              cambio de titular se hace en los TRES documentos a la vez. */}
        </div>
      </div>
    </footer>
  );
};
